/**
 * =============================================================================
 * Hook: useReferralAffiliatesPaginated
 * Versão: 2.0.0
 *
 * Descrição:
 *   Hook server-side para listagem paginada de afiliados (`affiliates`) do
 *   tenant ativo. Espelha o padrão de `useReferralPayoutsPaginated` /
 *   `useReferralCommissionsPaginated`:
 *
 *   - Chama a RPC `get_referral_affiliates_paginated`, que devolve em UMA
 *     única chamada: linhas paginadas (1 por pessoa) + total_count + KPIs
 *     globais (Total / Pendentes / Ativos / Saldo total) via window function.
 *   - 100% server-side: nenhum SELECT direto na tabela é feito a partir do
 *     frontend, evitando vazamento de dados sensíveis e o limite de 1k rows
 *     do client.
 *
 * Release notes:
 *   v1.0.0 - Criação do hook (substitui a query client-side de
 *            `useReferralAffiliates`, que passa a ser deprecada).
 *   v2.0.0 - Modelo N:N (Uma pessoa, vários programas):
 *            - Adiciona `memberships` (lista de adesões com programa, status
 *              e totais por programa) para alimentar o expand da tabela.
 *            - Adiciona `programs_count` (quantidade de adesões da pessoa).
 *            - Totais (saldo, ganho, pago, cliques, indicações) já vêm
 *              somados pela RPC; quando `program_id` é filtrado, os totais
 *              são restritos àquele programa.
 * =============================================================================
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type { AffiliateStatus } from '@/hooks/useReferralAffiliates';

// Logger centralizado para depuração
const logger = createLogger('ReferralAffiliatesPaginated');

/**
 * Adesão (membership) de um afiliado em um programa específico.
 * v2.0.0 — usado pelo expand da tabela e pelo drawer de detalhes para
 * permitir gestão por programa (aprovar/rejeitar/suspender por adesão).
 */
export interface AffiliateMembership {
  id: string;
  program_id: string;
  program_name: string | null;
  program_slug: string | null;
  status: AffiliateStatus;
  available_balance: number;
  total_earned: number;
  total_paid: number;
  /** v2.1.0 — receita total (sum de conversion_value) gerada por este membership. */
  total_sales: number;
  total_clicks: number;
  total_points: number;
  total_referrals: number;
  total_conversions: number;
  approved_at: string | null;
  created_at: string;
}

/**
 * Estrutura flat retornada pela RPC `get_referral_affiliates_paginated`.
 * Otimizada para uso direto na tabela (sem embeds aninhados).
 *
 * v2.0.0 — agora representa **1 linha por pessoa** (não mais por adesão).
 * Os totais já vêm agregados pela RPC. As adesões individuais ficam em
 * `memberships`.
 */
export interface PaginatedAffiliate {
  // Dados do afiliado
  id: string;
  tenant_id: string;
  // program_id "representativo" — quando há filtro de programa, é o próprio;
  // sem filtro, é uma das adesões da pessoa (uso compatível com o cliente atual).
  program_id: string | null;
  full_name: string | null;
  email: string | null;
  personal_slug: string | null;
  avatar_url: string | null;
  status: AffiliateStatus;
  available_balance: number;
  total_earned: number;
  total_paid: number;
  /** v2.1.0 — receita total gerada (sum de conversion_value das indicações convertidas). */
  total_sales: number;
  total_clicks: number;
  total_points: number;
  total_referrals: number;
  total_conversions: number;
  // v1.0.2: contagem de affiliate_links ativos (qualquer campanha) — usado pela
  // coluna "Link" para refletir o modelo campanha-driven (Bloco 5H).
  active_links_count: number;
  created_at: string;
  approved_at: string | null;
  last_login_at: string | null;
  /** v2.1.0 — última atividade no painel (heartbeat). Usado p/ presença online. */
  last_seen_at: string | null;

  // v2.0.0: adesões e quantidade de programas (modelo N:N)
  memberships: AffiliateMembership[];
  programs_count: number;

  // KPIs globais (mesmo valor em todas as rows)
  kpi_total: number;
  kpi_pending: number;
  kpi_active: number;
  kpi_total_balance: number;

  // Paginação (window function)
  total_count: number;
}

/** Filtros aceitos pela listagem de afiliados. */
export interface AffiliatesPaginatedFilters {
  status?: AffiliateStatus | null;
  program_id?: string | null;
  search?: string | null;
  only_with_balance?: boolean;
  created_from?: string | null;
  created_to?: string | null;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
}

/** KPIs agregados retornados pela RPC. */
export interface AffiliatesKpis {
  total: number;
  pending: number;
  active: number;
  totalBalance: number;
}

interface PaginationParams {
  page: number;
  pageSize: number;
}

export interface PaginatedAffiliatesResult {
  affiliates: PaginatedAffiliate[];
  totalCount: number;
  totalPages: number;
  kpis: AffiliatesKpis;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
}

const EMPTY_KPIS: AffiliatesKpis = {
  total: 0,
  pending: 0,
  active: 0,
  totalBalance: 0,
};

/**
 * Hook principal — listagem paginada de afiliados (server-side).
 *
 * @param filters    Filtros aplicados pela RPC.
 * @param pagination Página e tamanho da página.
 */
export function useReferralAffiliatesPaginated(
  filters: AffiliatesPaginatedFilters | undefined,
  pagination: PaginationParams,
): PaginatedAffiliatesResult {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [
      'referral-affiliates-paginated',
      currentTenant?.id,
      filters,
      pagination,
    ],
    queryFn: async () => {
      logger.debug('Buscando afiliados paginados', { filters, pagination });

      // Casting necessário até `types.ts` ser regenerado com a nova RPC
      const { data, error } = await (supabase.rpc as any)(
        'get_referral_affiliates_paginated',
        {
          p_page: pagination.page,
          p_page_size: pagination.pageSize,
          p_tenant_hint: currentTenant?.id || null,
          p_status: filters?.status || null,
          p_program_id: filters?.program_id || null,
          p_search: filters?.search || null,
          p_only_with_balance: filters?.only_with_balance ?? false,
          p_created_from: filters?.created_from || null,
          p_created_to: filters?.created_to || null,
          p_sort_by: filters?.sort_by || 'created_at',
          p_sort_dir: filters?.sort_dir || 'desc',
        },
      );

      if (error) {
        logger.error('Erro ao buscar afiliados paginados', error);
        throw error;
      }

      // Normaliza memberships/programs_count (a RPC pode devolver null/undefined)
      const rows = ((data || []) as PaginatedAffiliate[]).map((r) => ({
        ...r,
        memberships: Array.isArray(r.memberships) ? r.memberships : [],
        programs_count: Number(r.programs_count ?? 0),
      }));
      const totalCount = Number(rows[0]?.total_count ?? 0);

      const kpis: AffiliatesKpis = rows.length
        ? {
            total: Number(rows[0]?.kpi_total ?? 0),
            pending: Number(rows[0]?.kpi_pending ?? 0),
            active: Number(rows[0]?.kpi_active ?? 0),
            totalBalance: Number(rows[0]?.kpi_total_balance ?? 0),
          }
        : EMPTY_KPIS;

      return {
        affiliates: rows,
        totalCount,
        totalPages: Math.ceil(totalCount / pagination.pageSize) || 1,
        kpis,
      };
    },
    enabled: !!currentTenant?.id,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  return {
    affiliates: query.data?.affiliates || [],
    totalCount: query.data?.totalCount || 0,
    totalPages: query.data?.totalPages || 1,
    kpis: query.data?.kpis || EMPTY_KPIS,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error as Error | null,
    refetch: () => {
      queryClient.invalidateQueries({
        queryKey: ['referral-affiliates-paginated', currentTenant?.id],
      });
    },
  };
}
